import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  InputAdornment,
  Chip,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  ListSubheader,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AccountBalance, Close, Delete, Edit, FileDownload, PriceCheck, Undo } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import StandardPagination, {
  ITENS_POR_PAGINA_PADRAO,
} from "../components/Pagination/StandardPagination";
import SavingOverlay from "../components/SavingOverlay";
import MonthSelect from "../components/Inputs/MonthSelect";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, typography } from "../theme/designSystem";
import { dateFieldSx, filterFieldSx, searchFieldSx } from "../theme/filterStyles";
import {
  OpenProvision,
  accountsPayableService,
  AccountCategoryOption,
  AccountPayableItem,
  CashBookSlice,
  PaymentOriginOption,
} from "../services/accountsPayableService";

// Livro-caixa — lançamentos financeiros da entidade. Diferente do relatório de Fluxo de Caixa,
// que só mostra o efetivado, aqui a provisão também aparece: é nela que se dá a baixa quando o
// extrato confirma o pagamento.
//
// PADRÃO DESIGN: cabeçalho no painel, botões na barra de filtros, painel único, c4u-table com
// data-label, ResultCount, StandardPagination, colunas por prioridade e cartão no celular.

const formatCurrency = (value: number | null): string =>
  value === null || value === undefined
    ? "—"
    : value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/** Data pura: corta o horário sem converter fuso, senão o dia anda para trás no fuso local. */
const toDateInput = (iso: string | null): string => (iso ? iso.slice(0, 10) : "");

const formatDate = (iso: string | null): string => {
  const d = toDateInput(iso);
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
};

const currentMonth = (): string => new Date().toISOString().slice(0, 7);

/** Primeiro e último dia da competência: é ela que comanda o período. */
const monthRange = (month: string): { from: string; to: string } => {
  const [y, m] = month.split("-").map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, "0")}` };
};

interface FormState {
  id: string | null;
  name: string;
  notes: string;
  dateToPay: string;
  operationType: string;
  predictedValue: string;
  definitiveValue: string;
  paymentDate: string;
  paymentStatus: boolean;
  categoryId: string;
  subCategoryId: string;
  paymentOriginId: string;
  recurrence: string;
  recurrenceUnit: "month" | "week";
  /** marca de provisão: será quitada por OUTRO lançamento (passo 2); sem ela, baixa na própria linha */
  isProvision: boolean;
  /** passo 1 -> passo 2: abre a quitação da provisão recém-criada */
  settleNext: boolean;
  notConsiderInDre: boolean;
}

const emptyForm = (dateToPay: string): FormState => ({
  id: null,
  name: "",
  notes: "",
  dateToPay,
  operationType: "Débito",
  predictedValue: "",
  definitiveValue: "",
  paymentDate: "",
  paymentStatus: false,
  categoryId: "",
  subCategoryId: "",
  paymentOriginId: "",
  recurrence: "1",
  recurrenceUnit: "month",
  isProvision: false,
  settleNext: false,
  notConsiderInDre: false,
});

const formFromItem = (item: AccountPayableItem): FormState => ({
  id: item.id,
  name: item.name,
  notes: item.notes,
  dateToPay: toDateInput(item.dateToPay),
  operationType: item.operationType || "Débito",
  predictedValue: toMoneyInput(item.predictedValue),
  definitiveValue: toMoneyInput(item.definitiveValue),
  paymentDate: toDateInput(item.paymentDate),
  paymentStatus: item.paymentStatus,
  categoryId: item.categoryId ?? "",
  subCategoryId: item.subCategoryId ?? "",
  paymentOriginId: item.paymentOriginId ?? "",
  recurrence: "1",
  recurrenceUnit: "month",
  isProvision: item.isProvision,
  settleNext: false,
  notConsiderInDre: item.notConsiderInDre,
});

/** Botão redondo do sistema: a classe dá o tamanho, o sx dá a cor (padrão do PageModel). */
const rowActionSx = (background: string, hover: string) => ({
  backgroundColor: background,
  "&:hover": { backgroundColor: hover },
});

/** Máscara de moeda por centavos: só os dígitos contam, da direita para a esquerda -> "1.234,56". */
const maskMoney = (raw: string): string => {
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  return (parseInt(digits, 10) / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Número -> texto do campo de valor no mesmo formato da máscara. */
const toMoneyInput = (n: number | null): string =>
  n === null ? "" : n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Aceita "1.234,56" e "1234.56"; devolve null quando não dá para ler um número. */
const parseValue = (raw: string): number | null => {
  const cleaned = raw.trim().replace(/\s/g, "").replace(/\./g, "").replace(",", ".");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

const monthsAhead = (n: number): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const byName = (a: AccountCategoryOption, b: AccountCategoryOption) =>
  a.name.trim().localeCompare(b.name.trim(), "pt-BR", { sensitivity: "base" });

const CashBook: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();

  const [competence, setCompetence] = useState(currentMonth());
  const [from, setFrom] = useState(monthRange(currentMonth()).from);
  const [to, setTo] = useState(monthRange(currentMonth()).to);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [subCategoryFilter, setSubCategoryFilter] = useState("");
  const [operationFilter, setOperationFilter] = useState("");
  const [originFilter, setOriginFilter] = useState("");
  const [settlementFilter, setSettlementFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(ITENS_POR_PAGINA_PADRAO);

  const [items, setItems] = useState<AccountPayableItem[]>([]);
  const [total, setTotal] = useState(0);
  const [forecast, setForecast] = useState({ inflow: 0, outflow: 0, inflowCount: 0, outflowCount: 0 });
  const [realized, setRealized] = useState({ inflow: 0, outflow: 0, inflowCount: 0, outflowCount: 0 });
  // "Visualizar saldos" (pedido do Hilton, 13/09): conta a conta, no recorte do filtro, com a soma
  // conferida contra o Saldo do Realizado ao lado.
  const [balancesOpen, setBalancesOpen] = useState(false);
  const [balances, setBalances] = useState<CashBookSlice[] | null>(null);
  const [outOfResult, setOutOfResult] = useState<CashBookSlice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [categories, setCategories] = useState<AccountCategoryOption[]>([]);
  const [origins, setOrigins] = useState<PaymentOriginOption[]>([]);

  const [form, setForm] = useState<FormState | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toRemove, setToRemove] = useState<AccountPayableItem | null>(null);
  const [toUndo, setToUndo] = useState<AccountPayableItem | null>(null);
  // Justificativa obrigatória para desfazer a baixa e para excluir (decisão do Hilton, 13/09)
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);
  // Passo 2 (item 138): quitação ligada a uma provisão em aberto (ou pagamento direto, sem provisão)
  const [settleOpen, setSettleOpen] = useState(false);
  // Lançamento COMUM (sem marca de provisão): o $ da linha baixa a própria linha, como sempre foi
  const [settleRow, setSettleRow] = useState<{ id: string; name: string; operationType: string; predictedValue: number | null; dateToPay: string; categoryName: string; subCategoryName: string } | null>(null);
  // Aberto pelo $ da linha de uma provisão: o vínculo é com ela e o campo fica travado
  const [settleLocked, setSettleLocked] = useState(false);
  const [openProvisions, setOpenProvisions] = useState<OpenProvision[]>([]);
  const [settleForm, setSettleForm] = useState({
    provisionId: "",
    operationType: "Débito",
    name: "",
    categoryId: "",
    subCategoryId: "",
    paymentDate: "",
    value: "",
    paymentOriginId: "",
    comments: "",
    closes: true,
    confirmed: false,
  });
  const selectedProvision = useMemo(
    () => openProvisions.find((o) => o.id === settleForm.provisionId) ?? null,
    [openProvisions, settleForm.provisionId],
  );
  // Opção explícita "sem provisão" no combo: quem abriu pelo $ da linha precisa poder dizer que aquilo
  // não é uma provisão (erro no cadastro inicial) e quitar direto (pedido do Hilton, 13/09).
  const NO_PROVISION: OpenProvision = useMemo(() => ({
    id: "", dateToPay: "", name: "Sem provisão (pagamento direto)", operationType: "", categoryId: null,
    categoryName: "", subCategoryId: null, subCategoryName: "", predictedValue: 0, settledTotal: 0, remaining: 0, paymentOriginId: null,
    isProvision: true,
  }), []);
  const [settleError, setSettleError] = useState<string | null>(null);

  // Combos agrupados por tipo (Despesas, Impostos, Receitas) e em ordem alfabética: sem o título do grupo,
  // a ordem do servidor (grupo, nome) parecia embaralhada (pedido do Hilton, 02/11/2026)
  const categoryGroups = useMemo(() => {
    const groups = new Map<string, AccountCategoryOption[]>();
    categories
      .filter((c) => !c.parentCategoryId)
      .forEach((c) => {
        const key = c.groupName?.trim() || "Outros";
        groups.set(key, [...(groups.get(key) ?? []), c]);
      });
    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b, "pt-BR", { sensitivity: "base" }))
      .map(([group, items]) => ({ group, items: [...items].sort(byName) }));
  }, [categories]);

  // MUI Select só aceita os itens como filhos diretos: devolve a lista achatada, título + categorias
  const categoryMenuItems = useMemo(
    () =>
      categoryGroups.flatMap(({ group, items }) => [
        <ListSubheader
          key={`group-${group}`}
          sx={{ lineHeight: "32px", fontSize: "0.75rem", fontWeight: 700, color: colors.textSecondary, bgcolor: colors.backgroundSubtle }}
        >
          {group}
        </ListSubheader>,
        ...items.map((c) => (
          <MenuItem key={c.id} value={c.id}>
            {c.name}
          </MenuItem>
        )),
      ]),
    [categoryGroups],
  );

  const subCategoriesOf = useCallback(
    (categoryId: string) => categories.filter((c) => c.parentCategoryId === categoryId).sort(byName),
    [categories],
  );

  const hasFilterChanges =
    competence !== currentMonth() ||
    from !== monthRange(currentMonth()).from ||
    to !== monthRange(currentMonth()).to ||
    !!search ||
    !!categoryFilter ||
    !!subCategoryFilter ||
    !!operationFilter ||
    !!originFilter ||
    !!settlementFilter ||
    !!kindFilter;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await accountsPayableService.list(from, to, page, pageSize, {
        search: search.trim() || undefined,
        categoryId: categoryFilter || undefined,
        subCategoryId: subCategoryFilter || undefined,
        operation: operationFilter || undefined,
        paymentOriginId: originFilter || undefined,
        settlement: settlementFilter || undefined,
        kind: kindFilter || undefined,
      });
      setItems(data.items);
      setTotal(data.total);
      setForecast({
        inflow: data.forecastInflow,
        outflow: data.forecastOutflow,
        inflowCount: data.forecastInflowCount,
        outflowCount: data.forecastOutflowCount,
      });
      setOutOfResult(data.outOfResult || []);
      setRealized({
        inflow: data.realizedInflow,
        outflow: data.realizedOutflow,
        inflowCount: data.realizedInflowCount,
        outflowCount: data.realizedOutflowCount,
      });
    } catch {
      setError("Não foi possível carregar os lançamentos.");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    from,
    to,
    page,
    pageSize,
    search,
    categoryFilter,
    subCategoryFilter,
    operationFilter,
    originFilter,
    settlementFilter,
    kindFilter,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    accountsPayableService.categories().then(setCategories).catch(() => setCategories([]));
    accountsPayableService.paymentOrigins().then(setOrigins).catch(() => setOrigins([]));
  }, []);

  const [exporting, setExporting] = useState(false);

  const exportar = async () => {
    setExporting(true);
    try {
      const blob = await accountsPayableService.exportXlsx(from, to, {
        search: search.trim() || undefined,
        categoryId: categoryFilter || undefined,
        subCategoryId: subCategoryFilter || undefined,
        operation: operationFilter || undefined,
        paymentOriginId: originFilter || undefined,
        settlement: settlementFilter || undefined,
        kind: kindFilter || undefined,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `livro-caixa_${from}_a_${to}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Não foi possível gerar a planilha.");
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    const inicial = monthRange(currentMonth());
    setCompetence(currentMonth());
    setFrom(inicial.from);
    setTo(inicial.to);
    setSearch("");
    setCategoryFilter("");
    setSubCategoryFilter("");
    setOperationFilter("");
    setOriginFilter("");
    setSettlementFilter("");
    setKindFilter("");
    setPage(1);
  };

  const save = async () => {
    if (!form) return;
    const predicted = parseValue(form.predictedValue);
    const definitive = parseValue(form.definitiveValue);

    if (!form.name.trim()) {
      setFormError("Informe o nome do lançamento.");
      return;
    }
    if (!form.dateToPay) {
      setFormError("Informe a data.");
      return;
    }
    if (predicted === null || predicted <= 0) {
      setFormError("Informe um valor previsto maior que zero.");
      return;
    }
    if (form.id && form.paymentStatus && definitive === null) {
      setFormError("Lançamento marcado como pago precisa do valor efetivo.");
      return;
    }

    setFormError(null);
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.notes.trim() || null,
        dateToPay: form.dateToPay,
        predictedValue: predicted,
        predictedValueWithoutSurcharge: predicted,
        definitiveValue: definitive,
        definitiveValueWithoutSurcharge: definitive,
        paymentDate: form.paymentStatus ? form.paymentDate || form.dateToPay : null,
        paymentStatus: form.paymentStatus,
        accountPayableTypeId: form.categoryId || null,
        accountPayableSubTypeId: form.subCategoryId || null,
        paymentOriginId: form.paymentOriginId || null,
        operationType: form.operationType,
        recurrence: Number(form.recurrence) > 1 ? Number(form.recurrence) : null,
        recurrenceUnit: form.recurrenceUnit,
        isProvision: form.isProvision,
        notConsiderInDRE: form.notConsiderInDre,
      };

      if (form.id) {
        await accountsPayableService.update(form.id, payload);
        setForm(null);
        await load();
      } else {
        const createdId = await accountsPayableService.create(payload);
        const settleNext = form.settleNext;
        const operation = form.operationType;
        setForm(null);
        await load();
        if (settleNext && form.isProvision) {
          await openSettleFor(createdId, operation);
        }
      }
    } catch {
      setFormError("Não foi possível salvar o lançamento.");
    } finally {
      setSaving(false);
    }
  };

  /** Abre a efetivação já preenchida com o que o lançamento sabe; o usuário ajusta o que mudou. */
  /** Escolher a provisão preenche o que ela sabe; o valor sugerido é o que ainda falta dela. */
  const pickProvision = (id: string, list: OpenProvision[] = openProvisions) => {
    const prov = list.find((o) => o.id === id);
    if (prov && !prov.isProvision) {
      // lançamento comum escolhido no combo: vira baixa na própria linha, nada nasce órfão
      setSettleRow({ id: prov.id, name: prov.name, operationType: prov.operationType, predictedValue: prov.predictedValue, dateToPay: prov.dateToPay, categoryName: prov.categoryName, subCategoryName: prov.subCategoryName });
      setSettleForm((prev) => ({
        ...prev, provisionId: "", name: prov.name, value: toMoneyInput(prov.remaining), paymentOriginId: prov.paymentOriginId ?? prev.paymentOriginId,
        paymentDate: prev.paymentDate || new Date().toISOString().slice(0, 10),
      }));
      return;
    }
    setSettleForm((prev) => ({
      ...prev,
      provisionId: id,
      operationType: prov?.operationType || prev.operationType,
      name: prev.name,
      categoryId: prov?.categoryId ?? "",
      subCategoryId: prov?.subCategoryId ?? "",
      value: prov ? toMoneyInput(prov.remaining) : prev.value,
      paymentOriginId: prov?.paymentOriginId ?? prev.paymentOriginId,
      closes: true,
    }));
  };

  /** Abre o passo 2. `provisionId` pré-seleciona (botão da linha ou "registrar em seguida"). */
  const openSettleFor = async (provisionId: string | null, operationType?: string) => {
    setSettleError(null);
    setSettleRow(null);
    setSettleLocked(Boolean(provisionId));
    setSettleForm({
      provisionId: "",
      operationType: operationType || "Débito",
      name: "",
      categoryId: "",
      subCategoryId: "",
      paymentDate: new Date().toISOString().slice(0, 10),
      value: "",
      paymentOriginId: "",
      comments: "",
      closes: true,
      confirmed: false,
    });
    setSettleOpen(true);
    try {
      // só as provisões que vencem no período da tela (competência ou datas do filtro)
      const list = await accountsPayableService.openProvisions(from, to);
      setOpenProvisions(list);
      if (provisionId) pickProvision(provisionId, list);
    } catch {
      setOpenProvisions([]);
    }
  };

  const openSettle = (item: AccountPayableItem) => {
    if (item.isProvision) {
      return openSettleFor(item.id, item.operationType);
    }
    setSettleError(null);
    setSettleRow({ id: item.id, name: item.name, operationType: item.operationType, predictedValue: item.predictedValue, dateToPay: item.dateToPay, categoryName: item.categoryName, subCategoryName: item.subCategoryName });
    setSettleForm({
      provisionId: "",
      operationType: item.operationType || "Débito",
      name: item.name,
      categoryId: item.categoryId ?? "",
      subCategoryId: item.subCategoryId ?? "",
      paymentDate: toDateInput(item.dateToPay) || new Date().toISOString().slice(0, 10),
      // lançamento em aberto parte do previsto; o efetivo só existe em quem já teve baixa
      value: toMoneyInput(Math.abs((item.paymentStatus ? item.definitiveValue : null) ?? item.predictedValue ?? 0) || 0),
      paymentOriginId: item.paymentOriginId ?? "",
      comments: "",
      closes: true,
      confirmed: false,
    });
    setSettleOpen(true);
    return Promise.resolve();
  };

  const settle = async () => {
    const value = parseValue(settleForm.value);
    if (value === null || value <= 0) {
      setSettleError("Informe o valor real do pagamento.");
      return;
    }
    if (!settleForm.paymentDate) {
      setSettleError("Informe a data do pagamento.");
      return;
    }
    if (!settleForm.name.trim()) {
      setSettleError("Informe o nome do lançamento (ex.: PIX para a faxineira).");
      return;
    }
    if (!settleForm.confirmed) {
      setSettleError("Marque a confirmação para registrar.");
      return;
    }

    setSettleError(null);
    setSaving(true);
    // O usuário digita sempre o valor absoluto; o sinal é da operação (legado: débito negativo)
    const absValue = Math.abs(value);
    const signedValue = settleForm.operationType === "Débito" ? -absValue : absValue;
    try {
      if (settleRow) {
        // lançamento comum: baixa na própria linha (data, valor real e conta por onde passou)
        await accountsPayableService.settle(settleRow.id, {
          settled: true,
          paymentDate: settleForm.paymentDate,
          definitiveValue: signedValue,
          definitiveValueWithoutSurcharge: absValue,
          paymentOriginId: settleForm.paymentOriginId || null,
          comments: settleForm.comments.trim() || null,
        });
        setSettleOpen(false);
        await load();
        return;
      }
      // Com provisão: nasce a QUITAÇÃO ligada a ela (o servidor herda classificação e operação).
      // Sem provisão: pagamento direto, já efetivado.
      await accountsPayableService.create({
        name: settleForm.name.trim(),
        dateToPay: settleForm.paymentDate,
        predictedValue: absValue,
        predictedValueWithoutSurcharge: absValue,
        definitiveValue: signedValue,
        definitiveValueWithoutSurcharge: absValue,
        paymentDate: settleForm.paymentDate,
        paymentStatus: true,
        operationType: settleForm.operationType,
        accountPayableTypeId: settleForm.categoryId || null,
        accountPayableSubTypeId: settleForm.subCategoryId || null,
        paymentOriginId: settleForm.paymentOriginId || null,
        comments: settleForm.comments.trim() || null,
        provisionId: settleForm.provisionId || null,
        closesProvision: settleForm.provisionId ? settleForm.closes : null,
      });
      setSettleOpen(false);
      await load();
    } catch (e: any) {
      setSettleError(e?.response?.data?.description || "Não foi possível registrar o pagamento.");
    } finally {
      setSaving(false);
    }
  };

  /** Desfazer a baixa devolve o lançamento a Previsto; exige justificativa, que fica na observação. */
  const undoSettlement = async () => {
    if (!toUndo) return;
    if (!reason.trim()) { setReasonError("Informe a justificativa."); return; }
    setSaving(true);
    try {
      await accountsPayableService.settle(toUndo.id, { settled: false, comments: reason.trim() });
      setToUndo(null);
      setReason("");
      await load();
    } catch {
      setReasonError("Não foi possível desfazer a baixa.");
    } finally {
      setSaving(false);
    }
  };

  /** Exclusão LÓGICA (a linha fica na base, inativa) com justificativa obrigatória. */
  const remove = async () => {
    if (!toRemove) return;
    if (!reason.trim()) { setReasonError("Informe a justificativa."); return; }
    setSaving(true);
    try {
      await accountsPayableService.remove(toRemove.id, reason.trim());
      setToRemove(null);
      setReason("");
      await load();
    } catch (e: any) {
      setReasonError(e?.response?.data?.description || "Não foi possível excluir o lançamento.");
    } finally {
      setSaving(false);
    }
  };



  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#f8f9fa" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters>
          {/* 1. Cabeçalho: IRMÃO do painel — o modificador faz dele a faixa de cima do cartão */}
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">
                Livro-caixa
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Entradas e saídas pela data em que o dinheiro passou. Provisione na categoria real da
                despesa ou receita e depois registre a quitação; até lá a provisão fica como Previsto.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            {/* 3. Barra de filtros: a competência comanda o período */}
            {/* Filtros em duas linhas de largura total; ações no fim da segunda (pedido do Hilton, 13/09) */}
            <Box className="no-print" sx={{ display: "flex", flexDirection: "column", gap: "12px", mb: 2 }}>
              <Box sx={{ display: "flex", gap: "12px", flexWrap: { xs: "wrap", md: "nowrap" }, "& > *": { flex: "1 1 0 !important", minWidth: { xs: "100% !important", md: "120px !important" }, width: "auto !important" } }}>
                <MonthSelect
                  label="Competência"
                  value={competence}
                  onChange={(v) => {
                    const faixa = monthRange(v);
                    setCompetence(v);
                    setFrom(faixa.from);
                    setTo(faixa.to);
                    setPage(1);
                  }}
                  // Livro-caixa olha para a frente: as provisões dos próximos meses são o que se quer ver
                  // (pedido do Hilton, 13/09). Teto de 5 anos, o alcance de uma recorrência de 60 meses.
                  maxMonth={monthsAhead(60)}
                  sx={dateFieldSx}
                />
                <TextField
                  size="small"
                  type="date"
                  label="Data inicial"
                  value={from}
                  onChange={(e) => {
                    setFrom(e.target.value);
                    // Período livre: a competência deixa de descrever o recorte
                    setCompetence("");
                    setPage(1);
                  }}
                  fullWidth
                  sx={dateFieldSx}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="Data final"
                  value={to}
                  onChange={(e) => {
                    setTo(e.target.value);
                    setCompetence("");
                    setPage(1);
                  }}
                  fullWidth
                  sx={dateFieldSx}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  label="Busca por palavra"
                  placeholder="Nome ou observação"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  fullWidth
                  sx={searchFieldSx}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 60 }}
                />
                <TextField
                  select
                  size="small"
                  label="Categoria"
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(e.target.value);
                    setSubCategoryFilter("");
                    setPage(1);
                  }}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {categoryMenuItems}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Subcategoria"
                  value={subCategoryFilter}
                  onChange={(e) => {
                    setSubCategoryFilter(e.target.value);
                    setPage(1);
                  }}
                  fullWidth
                  disabled={!categoryFilter}
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {subCategoriesOf(categoryFilter).map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ display: "flex", gap: "12px", flexWrap: { xs: "wrap", md: "nowrap" }, alignItems: "flex-end", "& > .MuiFormControl-root": { flex: "1 1 0 !important", minWidth: { xs: "100% !important", md: "120px !important" }, width: "auto !important" } }}>
                <TextField
                  select
                  size="small"
                  label="Situação"
                  value={settlementFilter}
                  onChange={(e) => {
                    setSettlementFilter(e.target.value);
                    setPage(1);
                  }}
                  fullWidth
                  sx={dateFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">Todas</MenuItem>
                  <MenuItem value="settled">Efetivados</MenuItem>
                  <MenuItem value="pending">Previstos</MenuItem>
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Tipo"
                  value={kindFilter}
                  onChange={(e) => {
                    setKindFilter(e.target.value);
                    setPage(1);
                  }}
                  fullWidth
                  sx={dateFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="provision">Provisões</MenuItem>
                  <MenuItem value="regular">Lançamentos comuns</MenuItem>
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Operação"
                  value={operationFilter}
                  onChange={(e) => {
                    setOperationFilter(e.target.value);
                    setPage(1);
                  }}
                  fullWidth
                  sx={dateFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">Todas</MenuItem>
                  <MenuItem value="Crédito">Crédito</MenuItem>
                  <MenuItem value="Débito">Débito</MenuItem>
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Conta"
                  value={originFilter}
                  onChange={(e) => {
                    setOriginFilter(e.target.value);
                    setPage(1);
                  }}
                  fullWidth
                  sx={searchFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {origins.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.name}
                    </MenuItem>
                  ))}
                </TextField>
                <Box className="c4u-filters__actions" sx={{ marginLeft: { md: "auto" }, flex: "0 0 auto" }}>
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges || loading} />
                <Tooltip title="Baixar planilha do período">
                  <span>
                    <IconButton
                      onClick={exportar}
                      disabled={exporting || loading || items.length === 0}
                      sx={{
                        width: 40,
                        height: 40,
                        border: "1px solid #ced4da",
                        borderRadius: "4px",
                        color: colors.primary,
                      }}
                    >
                      {exporting ? <CircularProgress size={18} /> : <FileDownload sx={{ fontSize: "1.2rem" }} />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Registrar pagamento/recebimento (quitar uma provisão ou lançar um pagamento direto)">
                  <IconButton
                    onClick={() => openSettleFor(null)}
                    className="c4u-btn-icon c4u-btn-icon--outline"
                    aria-label="Registrar pagamento/recebimento"
                    sx={{ "&&": { color: colors.success, borderColor: colors.success }, "&&:hover": { backgroundColor: "rgba(40, 167, 69, 0.08)", borderColor: "#218838" } }}
                  >
                    <PriceCheck />
                  </IconButton>
                </Tooltip>
                <AddButton
                  onClick={() => {
                    setFormError(null);
                    setForm(emptyForm(from));
                  }}
                  title="Provisionar (novo lançamento)"
                />
                </Box>
              </Box>
            </Box>

            {/* Previsão e realizado lado a lado: são leituras diferentes do mesmo mês. */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
                mb: 2,
              }}
            >
              {[
                {
                  titulo: "Previsão",
                  ajuda: "Todos os lançamentos do mês, pelo valor previsto",
                  entrada: forecast.inflow,
                  saida: forecast.outflow,
                  qtdEntrada: forecast.inflowCount,
                  qtdSaida: forecast.outflowCount,
                },
                {
                  titulo: "Realizado",
                  ajuda: "Só os que tiveram baixa, pelo valor efetivo",
                  entrada: realized.inflow,
                  saida: realized.outflow,
                  qtdEntrada: realized.inflowCount,
                  qtdSaida: realized.outflowCount,
                },
              ].map((bloco) => (
                <Box
                  key={bloco.titulo}
                  sx={{
                    px: 2,
                    py: 1.25,
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    backgroundColor: "#fbfcfd",
                  }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#495057" }}>
                    {bloco.titulo}
                    <Typography component="span" sx={{ fontSize: 11, fontWeight: 400, color: "#adb5bd", ml: 1 }}>
                      {bloco.ajuda}
                    </Typography>
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mt: 0.5 }}>
                    {[
                      { label: `Entradas (${bloco.qtdEntrada})`, value: bloco.entrada, color: "#28a745" },
                      { label: `Saídas (${bloco.qtdSaida})`, value: bloco.saida, color: "#dc3545" },
                      {
                        label: "Saldo",
                        value: bloco.entrada - bloco.saida,
                        color: bloco.entrada - bloco.saida >= 0 ? "#28a745" : "#dc3545",
                      },
                    ].map((c) => (
                      <Box key={c.label}>
                        <Typography sx={{ fontSize: 11, color: "#6c757d", fontWeight: 600 }}>
                          {c.label}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 16, fontWeight: 600, color: c.color, fontVariantNumeric: "tabular-nums" }}
                        >
                          {formatCurrency(c.value)}
                        </Typography>
                      </Box>
                    ))}
                    {bloco.titulo === "Realizado" && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AccountBalance />}
                        onClick={() => {
                          setBalances(null);
                          setBalancesOpen(true);
                          accountsPayableService.balances(from, to).then(setBalances).catch(() => setBalances([]));
                        }}
                        sx={{ textTransform: "none", fontSize: 12, alignSelf: "center", ml: "auto" }}
                      >
                        Visualizar saldos
                      </Button>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>

            {/* O livro-caixa e um RAZAO: mostra tudo o que passou, inclusive o que nao e
                receita nem despesa. Sem esta linha, comparar com o Fluxo de Caixa parece
                erro de uma das telas -- e nao e. */}
            {outOfResult.length > 0 && (
              <Box
                sx={{
                  px: 2, py: 1.25, mb: 2, borderRadius: "8px",
                  border: "1px solid #ffe69c", backgroundColor: "#fffbf0",
                }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#856404" }}>
                  Fora do resultado
                  <Typography component="span" sx={{ fontSize: 11, fontWeight: 400, color: "#a07f2c", ml: 1 }}>
                    está nos totais acima, mas não é receita nem despesa — por isso o Fluxo de Caixa não conta
                  </Typography>
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mt: 0.5 }}>
                  {outOfResult.map((f) => (
                    <Box key={f.label}>
                      <Typography sx={{ fontSize: 11, color: "#856404", fontWeight: 600 }}>
                        {f.label} ({f.count})
                      </Typography>
                      <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#856404", fontVariantNumeric: "tabular-nums" }}>
                        {formatCurrency(f.value)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} />
              </Box>
            ) : error ? (
              <Typography sx={{ p: 3, fontSize: 14, color: "#dc3545" }}>{error}</Typography>
            ) : (
              <>
                <ResultCount shown={total} />

                {items.length === 0 ? (
                  <Typography sx={{ p: 3, fontSize: 14, color: "#6c757d" }}>
                    Nenhum lançamento nesta competência com os filtros aplicados.
                  </Typography>
                ) : (
                  <Box sx={{ backgroundColor: "#fff", borderRadius: "8px", overflow: "hidden", border: "1px solid #e2e8f0" }}>
                    {/* 8 e 9: colunas por prioridade, na ordem de sacrifício */}
                    <Box className="c4u-table__header">
                      <Box className="admin-plans-header-cell c4u-col--sm" sx={{ flex: "0 0 90px" }}>
                        Data
                      </Box>
                      <Box className="admin-plans-header-cell" sx={{ flex: "1 1 260px", minWidth: 140 }}>
                        Lançamento
                      </Box>
                      <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 1 170px", minWidth: 0 }}>
                        Categoria
                      </Box>
                      <Box className="admin-plans-header-cell c4u-col--xl" sx={{ flex: "0 1 120px", minWidth: 0 }}>
                        Conta
                      </Box>
                      <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 150px" }}>
                        Situação
                      </Box>
                      <Box className="admin-plans-header-cell" sx={{ flex: "0 0 110px" }}>
                        Valor
                      </Box>
                      <Box className="admin-plans-header-cell admin-plans-actions" sx={{ flex: "0 0 120px" }}>
                        Ações
                      </Box>
                    </Box>

                    {items.map((item) => {
                      const value = item.paymentStatus ? item.definitiveValue : item.predictedValue;
                      return (
                        <Box key={item.id} className="c4u-table__row">
                          <Box className="admin-plans-cell c4u-col--sm" data-label="Data" sx={{ flex: "0 0 90px" }}>
                            {formatDate(item.dateToPay)}
                          </Box>
                          <Box
                            className="admin-plans-cell"
                            data-label="Lançamento"
                            sx={{ flex: "1 1 260px", minWidth: 0 }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography className="admin-plans-name" sx={{ fontSize: 13, overflowWrap: "anywhere" }}>
                                {item.name || "—"}
                              </Typography>
                              {item.provisionId && (
                                <Typography sx={{ fontSize: 11, color: "#6c757d" }}>
                                  Quita a provisão de {formatDate(item.provisionDateToPay)}
                                  {item.closesProvision ? "" : " (parcial)"}
                                </Typography>
                              )}
                              {!item.paymentStatus && !item.provisionId && item.settledTotal > 0 && (
                                <Typography sx={{ fontSize: 11, color: "#6c757d" }}>
                                  Quitado {formatCurrency(item.settledTotal)} de {formatCurrency(item.predictedValue)}
                                </Typography>
                              )}
                              {item.notes && (
                                <Typography sx={{ fontSize: 11, color: "#adb5bd", overflowWrap: "anywhere" }}>
                                  {item.notes}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Box
                            className="admin-plans-cell c4u-col--lg"
                            data-label="Categoria"
                            sx={{ flex: "0 1 170px", minWidth: 0 }}
                          >
                            <Typography sx={{ fontSize: 12, color: "#6c757d", overflowWrap: "anywhere" }}>
                              {item.subCategoryName
                                ? `${item.categoryName} › ${item.subCategoryName}`
                                : item.categoryName || "—"}
                            </Typography>
                          </Box>
                          <Box
                            className="admin-plans-cell c4u-col--xl"
                            data-label="Conta"
                            sx={{ flex: "0 1 120px", minWidth: 0 }}
                          >
                            <Typography sx={{ fontSize: 12, color: "#6c757d", overflowWrap: "anywhere" }}>
                              {item.accountName || "—"}
                            </Typography>
                          </Box>
                          <Box
                            className="admin-plans-cell c4u-col--md"
                            data-label="Situação"
                            sx={{ flex: "0 0 150px" }}
                          >
                            {item.paymentStatus || item.provisionClosed ? (
                              <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#28a745" }}>
                                {item.paymentStatus ? "Efetivado" : "Provisão · quitada"}
                              </Typography>
                            ) : (
                              // Provisão aberta: a situação É o convite para quitar — quem lê "Previsto" precisa
                              // ver ali mesmo que o próximo passo é registrar o pagamento (dúvida do Hilton, 13/09).
                              <Tooltip title={item.isProvision ? "Registrar pagamento/recebimento desta provisão (lançamento novo)" : "Baixar este lançamento (na própria linha)"} arrow>
                                <Typography
                                  component="button"
                                  onClick={() => openSettle(item)}
                                  sx={{
                                    fontSize: 11, fontWeight: 600, color: "#fd7e14", background: "none", border: "1px dashed #fd7e14",
                                    borderRadius: "10px", padding: "1px 8px", cursor: "pointer", whiteSpace: "nowrap",
                                    "&:hover": { background: "rgba(253, 126, 20, 0.08)" },
                                  }}
                                >
                                  {item.isProvision
                                    ? (item.settledTotal > 0 ? "Provisão · parcial" : "Provisão · quitar")
                                    : "Previsto · baixar"}
                                </Typography>
                              </Tooltip>
                            )}
                          </Box>
                          <Box className="admin-plans-cell" data-label="Valor" sx={{ flex: "0 0 110px" }}>
                            <Typography
                              sx={{
                                fontSize: 13,
                                fontVariantNumeric: "tabular-nums",
                                color: item.operationType === "Crédito" ? "#28a745" : "#dc3545",
                              }}
                            >
                              {formatCurrency(value)}
                            </Typography>
                          </Box>
                          {/* 10. Ações: IconButton redondo; a linha NÃO é clicável */}
                          <Box
                            className="admin-plans-cell admin-plans-actions"
                            data-label="Ações"
                            sx={{ flex: "0 0 120px" }}
                          >
                            {/* Provisão aberta: quitar. Pago na própria linha (legado): desfazer a baixa.
                                Quitação ligada: some pela exclusão (a provisão reabre sozinha). */}
                            {!item.paymentStatus && !item.provisionClosed && (
                              <Tooltip title={item.isProvision ? "Registrar pagamento/recebimento desta provisão" : "Baixar este lançamento"}>
                                <IconButton
                                  className="c4u-row-action"
                                  size="small"
                                  onClick={() => openSettle(item)}
                                  sx={rowActionSx(colors.success, "#218838")}
                                >
                                  <PriceCheck sx={{ fontSize: "1rem" }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {item.paymentStatus && !item.provisionId && (
                              <Tooltip title="Desfazer a baixa">
                                <IconButton
                                  className="c4u-row-action"
                                  size="small"
                                  onClick={() => { setReason(""); setReasonError(null); setToUndo(item); }}
                                  sx={rowActionSx("#6c757d", "#5a6268")}
                                >
                                  <Undo sx={{ fontSize: "1rem" }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Editar lançamento">
                              <IconButton
                                className="c4u-row-action"
                                size="small"
                                onClick={() => {
                                  setFormError(null);
                                  setForm(formFromItem(item));
                                }}
                                sx={rowActionSx(colors.primary, colors.primaryHover)}
                              >
                                <Edit sx={{ fontSize: "1rem" }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Excluir lançamento">
                              <IconButton
                                className="c4u-row-action"
                                size="small"
                                onClick={() => { setReason(""); setReasonError(null); setToRemove(item); }}
                                sx={rowActionSx("#dc3545", "#c82333")}
                              >
                                <Delete sx={{ fontSize: "1rem" }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      );
                    })}

                  </Box>
                )}

                {items.length > 0 && (
                  <StandardPagination
                    currentPage={page}
                    totalPages={Math.max(1, Math.ceil(total / pageSize))}
                    itemsPerPage={pageSize}
                    totalItems={total}
                    onPageChange={setPage}
                    onItemsPerPageChange={(n) => {
                      setPageSize(n);
                      setPage(1);
                    }}
                  />
                )}
              </>
            )}
          </div>
        </Container>
      </Box>

      {/* 11. Modal: faixa ciano + ×, botões lado a lado à direita */}
      <Dialog
        open={form !== null}
        onClose={() => !saving && setForm(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}
      >
        <SavingOverlay open={saving} message="Salvando..." />
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: colors.white,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "1rem 1.25rem",
          }}
        >
          <Typography
            component="h3"
            sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}
          >
            {form?.id ? "Editar lançamento" : form?.isProvision ? "Provisionar (passo 1 de 2)" : "Novo lançamento"}
          </Typography>
          <IconButton onClick={() => setForm(null)} sx={{ color: colors.white, padding: "0.25rem" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          {form && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                // 12. Espaçamento de formulário: 12px na linha, 16px entre linhas
                columnGap: "12px",
                rowGap: "16px",
              }}
            >
              <TextField
                label="Nome do lançamento"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                size="small"
                inputProps={{ maxLength: 150 }}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
                helperText={form.id
                  ? "É este nome que aparece nos relatórios"
                  : "É este nome que aparece nos relatórios. Use a categoria REAL da despesa/receita. Marcado como provisão, fica Previsto até a quitação (chip Provisão · quitar na linha, ou o $ da barra); sem a marca, o $ da linha baixa o próprio lançamento."}
              />
              <TextField
                select
                label="Operação"
                value={form.operationType}
                onChange={(e) => setForm({ ...form, operationType: e.target.value })}
                size="small"
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="Crédito">Crédito (entrada)</MenuItem>
                <MenuItem value="Débito">Débito (saída)</MenuItem>
              </TextField>
              <TextField
                type="date"
                label="Data"
                value={form.dateToPay}
                onChange={(e) => setForm({ ...form, dateToPay: e.target.value })}
                size="small"
                InputLabelProps={{ shrink: true }}
                helperText="Define a competência do lançamento"
              />
              {!form.id && (
                <Box sx={{ display: "flex", gap: "12px" }}>
                  <TextField
                    label="Repetir"
                    value={form.recurrence}
                    onChange={(e) =>
                      setForm({ ...form, recurrence: e.target.value.replace(/\D/g, "").slice(0, 2) })
                    }
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ inputMode: "numeric" }}
                    helperText="1 = único; até 60"
                    sx={{ flex: "0 0 40%" }}
                  />
                  <TextField
                    select
                    label="Por"
                    value={form.recurrenceUnit}
                    onChange={(e) => setForm({ ...form, recurrenceUnit: e.target.value as "month" | "week" })}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    disabled={Number(form.recurrence) <= 1}
                    sx={{ flex: 1 }}
                  >
                    <MenuItem value="month">Mês</MenuItem>
                    <MenuItem value="week">Semana</MenuItem>
                  </TextField>
                </Box>
              )}
              <TextField
                select
                label="Categoria"
                value={form.categoryId}
                onChange={(e) => setForm({ ...form, categoryId: e.target.value, subCategoryId: "" })}
                size="small"
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">Sem categoria</MenuItem>
                {categoryMenuItems}
              </TextField>
              <TextField
                select
                label="Subcategoria"
                value={form.subCategoryId}
                onChange={(e) => setForm({ ...form, subCategoryId: e.target.value })}
                size="small"
                disabled={!form.categoryId}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">Sem subcategoria</MenuItem>
                {subCategoriesOf(form.categoryId).map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Conta"
                value={form.paymentOriginId}
                onChange={(e) => setForm({ ...form, paymentOriginId: e.target.value })}
                size="small"
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">Sem conta</MenuItem>
                {origins.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Valor previsto"
                value={form.predictedValue}
                onChange={(e) => setForm({ ...form, predictedValue: maskMoney(e.target.value) })}
                size="small"
                inputProps={{ maxLength: 15, inputMode: "decimal" }}
                InputLabelProps={{ shrink: true }}
                placeholder="0,00"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isProvision}
                    onChange={(e) => setForm({ ...form, isProvision: e.target.checked, settleNext: e.target.checked && form.settleNext })}
                    disabled={Boolean(form.id) && form.paymentStatus}
                  />
                }
                label="É provisão (será quitada depois por outro lançamento)"
                sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
              />
              {!form.id && form.isProvision && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.settleNext}
                      onChange={(e) => setForm({ ...form, settleNext: e.target.checked })}
                    />
                  }
                  label="Registrar a quitação em seguida"
                  sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
                />
              )}
              {form.id && form.paymentStatus && (
                <Typography sx={{ gridColumn: { xs: "auto", sm: "1 / -1" }, fontSize: 13, color: "#6c757d" }}>
                  Pago em {formatDate(form.paymentDate || null) || "—"} · valor efetivo {formatCurrency(parseValue(form.definitiveValue))}.
                  Para alterar o pagamento use "Desfazer a baixa" na linha e registre de novo.
                </Typography>
              )}
              <TextField
                label="Observação"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                size="small"
                multiline
                minRows={2}
                inputProps={{ maxLength: 500 }}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.notConsiderInDre}
                    onChange={(e) => setForm({ ...form, notConsiderInDre: e.target.checked })}
                  />
                }
                label="Não considerar na DRE"
                sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
              />

              {formError && (
                <Typography sx={{ gridColumn: { xs: "auto", sm: "1 / -1" }, fontSize: 13, color: "#dc3545" }}>
                  {formError}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        {form && (
          <DialogActions sx={{ padding: "1rem 1.25rem" }}>
            <Button variant="outlined" onClick={() => setForm(null)} sx={{ textTransform: "none" }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={save}
              sx={{ textTransform: "none", backgroundColor: colors.primary }}
            >
              {form.id ? "Salvar" : !form.isProvision ? "Salvar" : form.settleNext ? "Provisionar e quitar" : "Provisionar"}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Efetivação: a conciliação precisa dizer QUANDO, QUANTO e POR ONDE — confirmar o previsto
          no escuro não serve para extrato, que é justamente onde o valor sai diferente. */}
      <Dialog
        open={settleOpen}
        onClose={() => !saving && setSettleOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}
      >
        <SavingOverlay open={saving} message="Registrando..." />
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: colors.white,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "1rem 1.25rem",
          }}
        >
          <Typography
            component="h3"
            sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}
          >
            {settleRow ? "Baixar lançamento" : "Quitar (passo 2 de 2): registrar pagamento/recebimento"}
          </Typography>
          <IconButton onClick={() => setSettleOpen(false)} sx={{ color: colors.white, padding: "0.25rem" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          {settleOpen && settleRow && (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: "12px", rowGap: "16px" }}>
              <Box sx={{ gridColumn: { xs: "auto", sm: "1 / -1" }, p: 1.5, borderRadius: "8px", background: "#f8f9fa", border: "1px solid #e9ecef" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#212529" }}>{settleRow.name}</Typography>
                  <Chip
                    size="small"
                    label={settleRow.operationType === "Crédito" ? "Crédito (entrada)" : "Débito (saída)"}
                    sx={{ height: 22, fontWeight: 700, color: "white", backgroundColor: settleRow.operationType === "Crédito" ? "#28a745" : "#dc3545" }}
                  />
                </Box>
                <Typography sx={{ fontSize: 14, color: "#495057", mt: 0.5 }}>
                  {settleRow.subCategoryName ? `${settleRow.categoryName} › ${settleRow.subCategoryName}` : settleRow.categoryName || "Sem categoria"}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#6c757d" }}>
                  Previsto {formatCurrency(settleRow.predictedValue === null ? null : Math.abs(settleRow.predictedValue))} · vencimento {formatDate(settleRow.dateToPay)} · a baixa é registrada na própria linha
                </Typography>
              </Box>
              <TextField
                type="date"
                label="Data do pagamento"
                value={settleForm.paymentDate}
                onChange={(e) => setSettleForm({ ...settleForm, paymentDate: e.target.value })}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Valor real"
                value={settleForm.value}
                onChange={(e) => setSettleForm({ ...settleForm, value: maskMoney(e.target.value) })}
                size="small"
                inputProps={{ maxLength: 15, inputMode: "decimal" }}
                InputLabelProps={{ shrink: true }}
                placeholder="0,00"
                helperText={settleForm.operationType === "Débito" ? "Saída: o sinal é da operação" : "Entrada: o sinal é da operação"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ fontWeight: 700, color: settleForm.operationType === "Débito" ? "#dc3545" : "#28a745" }}>
                        {settleForm.operationType === "Débito" ? "−" : "+"}
                      </Typography>
                    </InputAdornment>
                  ),
                  sx: { "& input": { color: settleForm.operationType === "Débito" ? "#dc3545" : "#28a745", fontWeight: 600 } },
                }}
              />
              <TextField
                select
                label="Conta de entrada/saída"
                value={settleForm.paymentOriginId}
                onChange={(e) => setSettleForm({ ...settleForm, paymentOriginId: e.target.value })}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
              >
                <MenuItem value="">Selecione</MenuItem>
                {origins.map((o) => (
                  <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Comentário"
                value={settleForm.comments}
                onChange={(e) => setSettleForm({ ...settleForm, comments: e.target.value })}
                size="small"
                multiline
                minRows={2}
                inputProps={{ maxLength: 500 }}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
              />
              <FormControlLabel
                sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
                control={<Switch checked={settleForm.confirmed} onChange={(e) => setSettleForm({ ...settleForm, confirmed: e.target.checked })} />}
                label="Confirmo o pagamento/recebimento"
              />
              {settleError && (
                <Typography sx={{ gridColumn: { xs: "auto", sm: "1 / -1" }, fontSize: 13, color: "#dc3545" }}>{settleError}</Typography>
              )}
            </Box>
          )}
          {settleOpen && !settleRow && (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: "12px", rowGap: "16px" }}>
              <TextField
                select
                label="Operação"
                value={settleForm.operationType}
                onChange={(e) => setSettleForm({ ...settleForm, operationType: e.target.value, provisionId: "" })}
                size="small"
                InputLabelProps={{ shrink: true }}
                disabled={settleLocked || Boolean(settleForm.provisionId)}
                helperText={settleForm.provisionId ? "Definida pela provisão" : undefined}
              >
                <MenuItem value="Crédito">Crédito (entrada)</MenuItem>
                <MenuItem value="Débito">Débito (saída)</MenuItem>
              </TextField>
              <Autocomplete
                options={[NO_PROVISION, ...openProvisions.filter((o) => o.operationType === settleForm.operationType)]}
                value={selectedProvision ?? NO_PROVISION}
                onChange={(_, option) => pickProvision(option?.id ?? "")}
                getOptionLabel={(o) => (o.id ? `${o.isProvision ? "Provisão" : "Lançamento"} · ${formatDate(o.dateToPay)} · ${o.name} · ${o.isProvision ? "falta" : "previsto"} ${formatCurrency(o.remaining)}` : o.name)}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                disableClearable
                disabled={settleLocked}
                size="small"
                noOptionsText="Nenhuma provisão em aberto no período"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Provisão"
                    placeholder="Digite para buscar; vazio = pagamento direto"
                    InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                    helperText={selectedProvision
                      ? `Previsto ${formatCurrency(selectedProvision.predictedValue)} · já quitado ${formatCurrency(selectedProvision.settledTotal)} · falta ${formatCurrency(selectedProvision.remaining)}`
                      : "Provisões que vencem no período do filtro. Sem provisão = pagamento direto, já efetivado"}
                  />
                )}
              />
              <TextField
                label="Nome do lançamento"
                value={settleForm.name}
                onChange={(e) => setSettleForm({ ...settleForm, name: e.target.value })}
                size="small"
                inputProps={{ maxLength: 150 }}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
                helperText="Obrigatório: descreva o pagamento (ex.: PIX para a faxineira)"
                placeholder="Como o pagamento aparece no extrato"
                required
              />
              <TextField
                select
                label="Categoria"
                value={settleForm.categoryId}
                onChange={(e) => setSettleForm({ ...settleForm, categoryId: e.target.value, subCategoryId: "" })}
                size="small"
                InputLabelProps={{ shrink: true }}
                disabled={Boolean(settleForm.provisionId)}
              >
                <MenuItem value="">Sem categoria</MenuItem>
                {categoryMenuItems}
              </TextField>
              <TextField
                select
                label="Subcategoria"
                value={settleForm.subCategoryId}
                onChange={(e) => setSettleForm({ ...settleForm, subCategoryId: e.target.value })}
                size="small"
                InputLabelProps={{ shrink: true }}
                disabled={Boolean(settleForm.provisionId) || !settleForm.categoryId}
              >
                <MenuItem value="">Sem subcategoria</MenuItem>
                {subCategoriesOf(settleForm.categoryId).map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </TextField>
              <TextField
                type="date"
                label="Data do pagamento"
                value={settleForm.paymentDate}
                onChange={(e) => setSettleForm({ ...settleForm, paymentDate: e.target.value })}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Valor real"
                value={settleForm.value}
                onChange={(e) => setSettleForm({ ...settleForm, value: maskMoney(e.target.value) })}
                size="small"
                inputProps={{ maxLength: 15, inputMode: "decimal" }}
                InputLabelProps={{ shrink: true }}
                placeholder="0,00"
                helperText={settleForm.operationType === "Débito" ? "Saída: o sinal é da operação" : "Entrada: o sinal é da operação"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ fontWeight: 700, color: settleForm.operationType === "Débito" ? "#dc3545" : "#28a745" }}>
                        {settleForm.operationType === "Débito" ? "−" : "+"}
                      </Typography>
                    </InputAdornment>
                  ),
                  sx: { "& input": { color: settleForm.operationType === "Débito" ? "#dc3545" : "#28a745", fontWeight: 600 } },
                }}
              />
              <TextField
                select
                label="Conta de entrada/saída"
                value={settleForm.paymentOriginId}
                onChange={(e) => setSettleForm({ ...settleForm, paymentOriginId: e.target.value })}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
              >
                <MenuItem value="">Selecione</MenuItem>
                {origins.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Comentário"
                value={settleForm.comments}
                onChange={(e) => setSettleForm({ ...settleForm, comments: e.target.value })}
                size="small"
                multiline
                minRows={2}
                inputProps={{ maxLength: 500 }}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
                placeholder="Ex.: pago com juros, desconto negociado"
              />
              {settleForm.provisionId && (
                <FormControlLabel
                  sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
                  control={
                    <Switch
                      checked={settleForm.closes}
                      onChange={(e) => setSettleForm({ ...settleForm, closes: e.target.checked })}
                    />
                  }
                  label="Quita a provisão (não há mais parcelas)"
                />
              )}
              <FormControlLabel
                sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
                control={
                  <Switch
                    checked={settleForm.confirmed}
                    onChange={(e) => setSettleForm({ ...settleForm, confirmed: e.target.checked })}
                  />
                }
                label="Confirmo o pagamento/recebimento"
              />

              {settleError && (
                <Typography sx={{ gridColumn: { xs: "auto", sm: "1 / -1" }, fontSize: 13, color: "#dc3545" }}>
                  {settleError}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        {settleOpen && (
          <DialogActions sx={{ padding: "1rem 1.25rem" }}>
            <Button variant="outlined" onClick={() => setSettleOpen(false)} sx={{ textTransform: "none" }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={settle}
              disabled={!settleForm.confirmed}
              sx={{ textTransform: "none", backgroundColor: colors.primary }}
            >
              Registrar
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Saldos por conta no recorte do filtro; a soma tem de bater com o Saldo do Realizado */}
      <Dialog open={balancesOpen} onClose={() => setBalancesOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem" }}>
          <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            Saldos por conta
          </Typography>
          <IconButton onClick={() => setBalancesOpen(false)} sx={{ color: colors.white, padding: "0.25rem" }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          {balances === null ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={28} sx={{ color: colors.primary }} /></Box>
          ) : (() => {
            const soma = Math.round(balances.reduce((acc, b) => acc + b.value, 0) * 100) / 100;
            const saldoRealizado = Math.round((realized.inflow - realized.outflow) * 100) / 100;
            const bate = Math.abs(soma - saldoRealizado) < 0.01;
            return (
              <>
                <Typography sx={{ fontSize: 12, color: "#6c757d", mb: 1.5 }}>
                  Marcador de saldo do mês mais os lançamentos efetivados até {formatDate(to)}, por conta (RN-208b).
                </Typography>
                {balances.length === 0 ? (
                  <Typography sx={{ fontSize: 12, color: "#adb5bd" }}>Nenhuma conta ativa.</Typography>
                ) : (
                  balances.map((b) => (
                    <Box key={b.label} sx={{ display: "flex", justifyContent: "space-between", gap: 1, py: 0.5, borderBottom: "1px solid #f1f3f5" }}>
                      <Typography sx={{ fontSize: 13, color: "#495057", minWidth: 0, overflowWrap: "anywhere" }}>
                        {b.label}
                        <Typography component="span" sx={{ fontSize: 11, color: "#adb5bd", ml: 0.75 }}>({b.count})</Typography>
                      </Typography>
                      <Typography sx={{ fontSize: 13, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", color: b.value >= 0 ? "#28a745" : "#dc3545" }}>
                        {formatCurrency(b.value)}
                      </Typography>
                    </Box>
                  ))
                )}
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, pt: 1, borderTop: "2px solid #e2e8f0", mt: 0.5 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#495057" }}>Soma das contas</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: soma >= 0 ? "#28a745" : "#dc3545" }}>
                    {formatCurrency(soma)}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 12, color: bate ? "#28a745" : "#dc3545", mt: 0.5 }}>
                  {bate
                    ? `Bate com o Saldo do Realizado (${formatCurrency(saldoRealizado)}).`
                    : `Não bate com o Saldo do Realizado (${formatCurrency(saldoRealizado)}): diferença de ${formatCurrency(soma - saldoRealizado)}. Confere só quando o período começa no dia 1 e fica dentro do mês.`}
                </Typography>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button variant="outlined" onClick={() => setBalancesOpen(false)} sx={{ textTransform: "none" }}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Justificativa obrigatória para excluir (lógica) e para desfazer a baixa */}
      <Dialog open={toRemove !== null || toUndo !== null} onClose={() => !saving && (setToRemove(null), setToUndo(null))} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}>
        <SavingOverlay open={saving} message="Registrando..." />
        <DialogTitle sx={{ backgroundColor: toRemove ? "#dc3545" : colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem" }}>
          <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            {toRemove ? "Excluir lançamento" : "Desfazer a baixa"}
          </Typography>
          <IconButton onClick={() => { setToRemove(null); setToUndo(null); }} sx={{ color: colors.white, padding: "0.25rem" }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          <Typography sx={{ fontSize: 14, mb: 2 }}>
            {toRemove
              ? `"${toRemove.name}" sai da lista e dos relatórios. A linha fica guardada na base, inativa, com a justificativa.`
              : `"${toUndo?.name ?? ""}" volta a Previsto e sai do Fluxo de Caixa e da DRE. A justificativa fica na observação.`}
          </Typography>
          <TextField
            label="Justificativa"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
            required
            inputProps={{ maxLength: 300 }}
            InputLabelProps={{ shrink: true }}
            placeholder={toRemove ? "Ex.: lançado em duplicidade" : "Ex.: pagamento estornado pelo banco"}
            error={Boolean(reasonError)}
            helperText={reasonError ?? "Obrigatória"}
          />
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button variant="outlined" onClick={() => { setToRemove(null); setToUndo(null); }} sx={{ textTransform: "none" }}>Cancelar</Button>
          <Button variant="contained" onClick={toRemove ? remove : undoSettlement} sx={{ textTransform: "none", backgroundColor: toRemove ? "#dc3545" : colors.primary }}>
            {toRemove ? "Excluir" : "Desfazer"}
          </Button>
        </DialogActions>
      </Dialog>

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default CashBook;
